// Modal "Parceiros de viagem" — gerencia membros + convites de uma trip.
//
// Mostra:
//   1. Membros que já aceitaram (de trip.memberIds + trip.members)
//   2. Convites em aberto (de trip.pendingInvites)
//   3. Form pra adicionar mais emails → cria invites/{token} + atualiza
//      pendingInvites no doc da trip
//
// Cada link de convite tem botão Copiar + WhatsApp (mesmo padrão do
// newTripModal pós-criação da viagem).
//
// API:
//   await openPartnersModal(trip)
//   → resolve quando user fecha (com a trip atualizada). Não retorna nada.

import { auth, db } from '../../core/firebase.js';
import {
  doc, updateDoc, getDoc, setDoc, serverTimestamp, deleteDoc,
  arrayRemove, deleteField,
} from 'firebase/firestore';
import { renderAvatarHtml, renderOtherAvatarHtml, hydrateAvatarPhotos } from '../profile/avatar.js';
import { getUserSettings, resolveDisplayName } from '../../core/userSettings.js';
import { inviteUrl } from '../../core/site.js';
import { createUserInviteAutocomplete } from '../../shared/components/userInviteAutocomplete.js';
import { toast } from '../../shared/components/toast.js';

const ICONS = {
  close:  '<path d="M18 6L6 18M6 6l12 12"/>',
  user:   '<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-7 8-7s8 3 8 7"/>',
  copy:   '<rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>',
  check:  '<path d="M20 6L9 17l-5-5"/>',
  trash:  '<path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>',
  star:   '<path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z"/>',
  enter:  '<path d="M9 10l-4 4 4 4M5 14h12a4 4 0 0 0 4-4V5"/>',
};

const WHATSAPP_SVG = `
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#25D366" d="M20.52 3.449C18.24 1.245 15.24 0.03 12.045 0 5.463 0 .104 5.334.101 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.581 0 11.94-5.335 11.943-11.893A11.821 11.821 0 0 0 20.52 3.45zM12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.437-9.884 9.889-9.884a9.825 9.825 0 0 1 6.991 2.898 9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.438 9.884-9.89 9.884zm5.422-7.403c-.297-.149-1.758-.867-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.173.198-.297.298-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01a1.094 1.094 0 0 0-.793.371c-.272.297-1.04 1.016-1.04 2.479s1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
  </svg>
`;

const MAX_MEMBERS = 10;

export function openPartnersModal(initialTrip) {
  return new Promise((resolve) => {
    let trip = { ...initialTrip };

    // Convite aceito (o email virou membro) não é mais "em aberto". O fluxo
    // de aceite (invite.js) não limpa pendingInvites, então fazemos isso
    // aqui: filtra o estado local antes do 1º render e poda o doc (best-
    // effort — só o admin tem permissão de escrever pendingInvites).
    const _rawPending = trip.pendingInvites || [];
    const _cleanPending = filterUnacceptedInvites(_rawPending, trip.members);
    if (_cleanPending.length !== _rawPending.length) {
      trip = { ...trip, pendingInvites: _cleanPending };
      updateDoc(doc(db, 'trips', trip.id), { pendingInvites: _cleanPending })
        .catch((err) => console.warn('[partners] poda de pendingInvites falhou:', err));
    }

    // Instância atual do autocomplete de convite (componente compartilhado).
    // Re-criado a cada rerender; destruído ao fechar (limpa listener global).
    let inviteAc = null;

    const overlay = document.createElement('div');
    overlay.className = 'settings-modal partners-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    document.body.appendChild(overlay);

    function rerender() {
      overlay.innerHTML = MODAL_HTML(trip);
      hydrateAvatarPhotos(overlay);
      mountInvite();
    }

    function destroyInvite() {
      if (inviteAc) { try { inviteAc.destroy(); } catch {} inviteAc = null; }
    }

    function close() {
      document.removeEventListener('keydown', onKey);
      destroyInvite();
      overlay.remove();
      resolve();
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);

    // Monta o autocomplete de convite (componente compartilhado) no slot.
    // Toda a lógica de domínio (criar invites/{token} amarrado ao email +
    // atualizar trip.pendingInvites) fica AQUI, no caller, via onInvite.
    function mountInvite() {
      destroyInvite();
      const mount = overlay.querySelector('[data-slot="invite-mount"]');
      if (!mount) return;

      const excludeEmails = [
        ...(trip.pendingInvites || []).map((i) => i.email),
        ...Object.values(trip.members || {}).map((m) => m.email),
      ].filter(Boolean);

      inviteAc = createUserInviteAutocomplete({
        placeholder: 'Nome ou email da pessoa',
        excludeEmails,
        onInvite: async ({ email }) => {
          const totalSlots = (trip.memberIds?.length || 0) + (trip.pendingInvites?.length || 0);
          if (totalSlots >= MAX_MEMBERS) {
            throw new Error(`Limite de ${MAX_MEMBERS} pessoas por viagem.`);
          }
          const token = _randomToken();
          const adminEmail = auth.currentUser?.email || null;
          await setDoc(doc(db, 'invites', token), {
            tripId: trip.id,
            createdBy: auth.currentUser.uid,
            createdByEmail: adminEmail,
            email,                    // convite amarrado a este endereço
            guestEmail: email,        // compat com campo legado
            tripSummary: {
              name: trip.name,
              startDate: trip.startDate || null,
              endDate: trip.endDate || null,
            },
            createdAt: serverTimestamp(),
            expiresAt: null,
            revoked: false,
          });
          const newInvite = { email, token };
          const next = [...(trip.pendingInvites || []), newInvite];
          await updateDoc(doc(db, 'trips', trip.id), { pendingInvites: next });
          trip = { ...trip, pendingInvites: next };
          rerender();
          toast.success('Convite enviado');
        },
      });
      mount.appendChild(inviteAc.element);
    }

    function bind() {
      overlay.addEventListener('click', async (e) => {
        if (e.target === overlay) close();
        const action = e.target.closest('[data-action]')?.dataset.action;
        if (!action) return;

        if (action === 'close') { e.preventDefault(); close(); return; }

        if (action === 'copy') {
          e.preventDefault();
          const btn = e.target.closest('[data-action="copy"]');
          const url = btn?.dataset.url;
          if (!url) return;
          try {
            await navigator.clipboard.writeText(url);
            const orig = btn.innerHTML;
            btn.innerHTML = `${svgIcon('check', 14)} Copiado`;
            setTimeout(() => { btn.innerHTML = orig; }, 1500);
          } catch {}
          return;
        }

        if (action === 'invite-revoke') {
          e.preventDefault();
          const btn = e.target.closest('[data-action="invite-revoke"]');
          const token = btn?.dataset.token;
          if (!token) return;
          if (!confirm('Revogar este convite? O link fica inválido.')) return;

          // Disable o botão durante a operação pra evitar duplo-clique
          if (btn) { btn.disabled = true; btn.style.opacity = '0.5'; }

          // Estratégia em 2 passos com retry. Primeiro deleta o invite (rules
          // permitem se admin do trip). Depois atualiza o array pendingInvites.
          // Se o delete falhar transientemente (rede), tenta 2x mais.
          let lastErr = null;
          let deleted = false;
          for (let attempt = 0; attempt < 3 && !deleted; attempt++) {
            try {
              await deleteDoc(doc(db, 'invites', token));
              deleted = true;
            } catch (err) {
              lastErr = err;
              await new Promise((r) => setTimeout(r, 250 * (attempt + 1)));
            }
          }

          if (!deleted) {
            console.error('[partners] revoke deleteDoc failed after retry:', lastErr?.code, lastErr?.message, lastErr);
            if (btn) { btn.disabled = false; btn.style.opacity = ''; }
            alert(`Não consegui revogar: ${lastErr?.message || 'erro desconhecido'}`);
            return;
          }

          // Update do array pendingInvites — best-effort. Mesmo se falhar, o
          // invite já foi removido do Firestore, então o link fica morto.
          const next = (trip.pendingInvites || []).filter((i) => i.token !== token);
          try {
            await updateDoc(doc(db, 'trips', trip.id), { pendingInvites: next });
          } catch (err) {
            console.warn('[partners] update pendingInvites failed (invite já removido):', err?.code, err?.message);
          }
          trip = { ...trip, pendingInvites: next };
          rerender();
          return;
        }

        if (action === 'member-make-admin') {
          e.preventDefault();
          const btn = e.target.closest('[data-action="member-make-admin"]');
          const uid = btn?.dataset.uid;
          if (!uid) return;
          const m = (trip.members || {})[uid] || {};
          const name = m.name || nameFromEmail(m.email);
          if (!confirm(
            `Transferir a administração da viagem para ${name}? `
            + 'Você deixa de ser o admin e essa pessoa passa a poder editar tudo, '
            + 'incluindo excluir a viagem.')) return;
          if (btn) { btn.disabled = true; btn.style.opacity = '0.5'; }
          try {
            const oldUid = auth.currentUser.uid;
            await updateDoc(doc(db, 'trips', trip.id), {
              userId: uid,
              [`members.${uid}.role`]: 'admin',
              [`members.${oldUid}.role`]: 'member',
            });
            const nextMembers = { ...(trip.members || {}) };
            nextMembers[uid] = { ...(nextMembers[uid] || {}), role: 'admin' };
            nextMembers[oldUid] = { ...(nextMembers[oldUid] || {}), role: 'member' };
            trip = { ...trip, userId: uid, members: nextMembers };
            rerender();
            toast.success('Administração transferida');
          } catch (err) {
            console.error('[partners] make-admin failed:', err);
            if (btn) { btn.disabled = false; btn.style.opacity = ''; }
            alert('Não consegui transferir a administração: ' + (err?.message || 'erro desconhecido'));
          }
          return;
        }

        if (action === 'member-remove') {
          e.preventDefault();
          const btn = e.target.closest('[data-action="member-remove"]');
          const uid = btn?.dataset.uid;
          if (!uid) return;
          if (!confirm('Remover esta pessoa da viagem? Ela perde o acesso.')) return;
          if (btn) { btn.disabled = true; btn.style.opacity = '0.5'; }
          try {
            await updateDoc(doc(db, 'trips', trip.id), {
              memberIds: arrayRemove(uid),
              [`members.${uid}`]: deleteField(),
            });
            const nextIds = (trip.memberIds || []).filter((x) => x !== uid);
            const nextMembers = { ...(trip.members || {}) };
            delete nextMembers[uid];
            trip = { ...trip, memberIds: nextIds, members: nextMembers };
            rerender();
            toast.success('Parceiro removido');
          } catch (err) {
            console.error('[partners] member-remove failed:', err);
            if (btn) { btn.disabled = false; btn.style.opacity = ''; }
            alert('Não consegui remover: ' + (err?.message || 'erro desconhecido'));
          }
          return;
        }

      });
    }

    // Listener de clique ligado UMA vez (delegação via data-action) — antes
    // era re-anexado a cada rerender, empilhando handlers duplicados.
    bind();
    rerender();

    // Fetch settings do user logado em paralelo — usado pra mostrar a
    // própria foto via avatarVersion. Não bloqueia o render inicial; quando
    // resolve, re-renderiza com a foto.
    getUserSettings().then((settings) => {
      trip = { ...trip, _selfSettings: settings };
      rerender();
    }).catch(() => {});
  });
}

// ---------- HTML ----------

function MODAL_HTML(trip) {
  const memberIds = trip.memberIds || [];
  const members = trip.members || {};
  const pending = trip.pendingInvites || [];
  const totalSlots = memberIds.length + pending.length;
  const canAdd = totalSlots < MAX_MEMBERS;

  return `
    <form class="settings-modal-card settings-modal-card--lg partners-card glass-sheet">
      <button type="button" class="newtrip-close" data-action="close" aria-label="Fechar">
        ${svgIcon('close', 16)}
      </button>

      <div class="settings-modal-head">
        <p class="eyebrow settings-modal-eyebrow">${esc(trip.name || 'Viagem')}</p>
        <h3 class="h2 settings-modal-title">Parceiros de viagem</h3>
        <p class="settings-modal-help">${memberIds.length} membro${memberIds.length === 1 ? '' : 's'} · ${pending.length} convite${pending.length === 1 ? '' : 's'} pendente${pending.length === 1 ? '' : 's'} · limite ${MAX_MEMBERS}</p>
      </div>

      <div class="newtrip-body">
        ${MEMBERS_HTML(trip, memberIds, members)}
        ${pending.length ? PENDING_HTML(trip, pending) : ''}
        ${canAdd ? ADD_FORM_HTML() : `<p class="newtrip-hint">Limite de ${MAX_MEMBERS} pessoas atingido. Revogue um convite ou remova um membro pra adicionar mais.</p>`}
      </div>

      <div class="settings-modal-foot newtrip-foot">
        <button type="button" class="btn btn-primary" data-action="close">Pronto</button>
      </div>
    </form>
  `;
}

function MEMBERS_HTML(trip, memberIds, members) {
  if (!memberIds.length) return '';
  const selfUid = auth.currentUser?.uid;
  // Só o dono da viagem pode remover parceiros.
  const isOwnerViewer = selfUid && selfUid === trip.userId;
  return `
    <section class="partners-section">
      <div class="partners-section-head">
        <span class="eyebrow">Já no roteiro</span>
        <span class="chip">${memberIds.length}</span>
      </div>
      <ul class="partners-list">
        ${memberIds.map((uid) => {
          const m = members[uid] || { email: '(sem email)', role: 'member' };
          const isAdmin = m.role === 'admin' || uid === trip.userId;
          const isSelf = uid === selfUid;
          // Botão remover: visível pro dono, pra parceiros que não são ele
          // mesmo nem o admin.
          const canRemove = isOwnerViewer && !isSelf && !isAdmin;
          // Nome: members[uid].name (se gravado) → senão extrai do email
          // (parte antes do @, capitalizada). Email continua aparecendo
          // embaixo como segundo nível de identificação.
          const displayName = m.name || nameFromEmail(m.email);
          // Avatar: pra self usamos settings (tem avatarVersion). Pra
          // outros usamos URL sem version (rules bloqueiam ler users/{uid}
          // alheio). Em ambos casos hydrateAvatarPhotos() faz preflight e
          // cai pra iniciais se 404.
          const avatarHtml = isSelf
            ? renderAvatarHtml({ uid, settings: trip._selfSettings, name: displayName })
            : renderOtherAvatarHtml({ uid, name: displayName });
          return `
            <li class="partners-item">
              <span class="avatar partners-avatar">${avatarHtml}</span>
              <span class="partners-info">
                <span class="partners-name">${esc(displayName)}${isSelf ? ' <span class="partners-you">(você)</span>' : ''}</span>
                <span class="partners-email mono">${esc(m.email || '')}</span>
              </span>
              ${isAdmin
                ? `<span class="partners-role">${svgIcon('star', 11)} admin</span>`
                : (canRemove
                  ? `<div class="partners-member-actions">
                       <button type="button" class="partners-member-action"
                         data-action="member-make-admin" data-uid="${esc(uid)}"
                         aria-label="Tornar admin" title="Tornar admin da viagem">
                         ${svgIcon('star', 14)}
                       </button>
                       <button type="button" class="partners-member-remove"
                         data-action="member-remove" data-uid="${esc(uid)}"
                         aria-label="Remover da viagem" title="Remover da viagem">
                         ${svgIcon('trash', 14)}
                       </button>
                     </div>`
                  : '')}
            </li>
          `;
        }).join('')}
      </ul>
    </section>
  `;
}

// Extrai um nome amigável a partir do email — "ana.martins@x.com" → "Ana Martins".
// Usado quando members[uid].name não foi gravado (caso atual de membros antigos).
function nameFromEmail(email) {
  const local = String(email || '').split('@')[0] || '';
  return local
    .replace(/[._-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(' ') || 'Membro';
}

function PENDING_HTML(trip, invites) {
  return `
    <section class="partners-section">
      <div class="partners-section-head">
        <span class="eyebrow">Convites em aberto</span>
        <span class="chip">${invites.length}</span>
      </div>
      <ul class="partners-list partners-list--invites">
        ${invites.map((inv) => {
          const url = inviteUrl(inv.token); // base pública do site (não localhost no app)
          const wa = whatsappUrl(url, trip.name);
          return `
            <li class="partners-invite">
              <div class="partners-invite-head">
                <span class="partners-email mono">${esc(inv.email)}</span>
                <button class="partners-invite-revoke" type="button" data-action="invite-revoke"
                  data-token="${esc(inv.token)}" aria-label="Revogar convite" title="Revogar convite">
                  ${svgIcon('trash', 14)}
                </button>
              </div>
              <input type="text" readonly value="${esc(url)}" class="input partners-invite-url"/>
              <div class="partners-invite-share">
                <a class="partners-invite-wa" href="${esc(wa)}" target="_blank" rel="noopener noreferrer"
                  aria-label="Compartilhar por WhatsApp" title="Compartilhar por WhatsApp">
                  ${WHATSAPP_SVG} <span>WhatsApp</span>
                </a>
                <button class="partners-invite-copy" type="button" data-action="copy" data-url="${esc(url)}">
                  ${svgIcon('copy', 14)} <span>Copiar</span>
                </button>
              </div>
            </li>
          `;
        }).join('')}
      </ul>
    </section>
  `;
}

function ADD_FORM_HTML() {
  return `
    <section class="partners-section">
      <div class="partners-section-head">
        <span class="eyebrow">Convidar mais</span>
      </div>
      <div data-slot="invite-mount"></div>
      <p class="newtrip-hint">Busque alguém pelo nome ou email — ou digite um email novo e aperte Enter. Cada convite vira um link único pra compartilhar por WhatsApp ou copiar.</p>
    </section>
  `;
}

// ---------- helpers ----------

// Remove da lista de convites em aberto aqueles cujo email já virou membro
// (= o convite foi aceito). Comparação case-insensitive.
function filterUnacceptedInvites(pending, members) {
  const memberEmails = new Set(
    Object.values(members || {})
      .map((m) => String(m?.email || '').toLowerCase())
      .filter(Boolean),
  );
  return (pending || []).filter(
    (inv) => inv && !memberEmails.has(String(inv.email || '').toLowerCase()),
  );
}

function whatsappUrl(inviteUrl, tripName) {
  const text = `Olá! Te convidei pra acompanhar a viagem "${tripName || 'Mapiare'}" no Mapiare. ` +
               `Acesse o link e crie sua conta: ${inviteUrl}`;
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

function _randomToken() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function svgIcon(name, size = 16) {
  const paths = ICONS[name];
  if (!paths) return '';
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;
}

function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
